import { trackRedeemClick, trackRewardDiscoverMoreClick } from "@loyalty/utils";

import { EmojiFaceSadIcon } from "@jti/brand-icons/emoji-face-sad";
import { type GetPrizesElementResponseType } from "@jti/rest-api-types";
import { useSelector } from "@jti/store/reduxHooks";
import { type RootStoreState } from "@jti/store/types";
import { VirtualPrizeBadges } from "@jti/ui/components/VirtualPrizeBadges";
import { useIntlNumberFormat } from "@jti/ui/hooks/useIntlNumberFormat";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { normalizeDate } from "@jti/ui/utils/formatDate";
import { validateLimitedTimeBadgesData } from "@jti/ui/utils/validateBadgeData";

import { usePrizeCta } from "../../../hooks/useLoyaltyPrizeCta/useLoyaltyPrizeCta";
import { ImageInfoBanner } from "../../ImageInfoBanner/ImageInfoBanner";
import { PointsProgressBar } from "../../PointsProgressBar/PointsProgressBar";
import { RewardCard } from "../../RewardCard/RewardCard";
import { getPrizeColor, isLimitedOfferEnabled, validateTierRestriction } from "../util/simplifedPrizesUtils";

import type { ItemsListProps } from "./ItemsList.types";

import styles from "./ItemsList.module.scss";

export const ItemsList: React.FC<ItemsListProps> = ({
  items,
  pointsLabel,
  ctaLabel,
  noOffersText,
  earnPointsCtaText,
  earnPointsCtaUrl,
  color,
  points,
  setDialogData,
  newBadgeData,
  limitedOfferLabels,
  tier,
  outOfStockLabel,
  isLogged,
  discoverUrlParent,
  discoverButtonText,
  prizeCardSizeVariant = "default",
  prizeCardVariant = "default",
  isLegacy = false,
  noOffersSubtext,
}: ItemsListProps) => {
  const intl = useIntlNumberFormat();
  const isRestricted = useSelector((state: RootStoreState) => state.loyaltyCore.isRestricted);
  const shouldHideButton = !isLogged || isRestricted;
  const { __ } = useTranslations();
  const getPrizeCta = usePrizeCta();

  const handleOnClick = (item: GetPrizesElementResponseType) => {
    setDialogData(item);
    trackRedeemClick(item);
  };

  const handleDiscoverMoreClick = (item: GetPrizesElementResponseType) => {
    trackRewardDiscoverMoreClick(item);
  };

  return items.length > 0 ? (
    <div className={styles.itemsList} data-testid={"simplifiedPrizes"}>
      {items.map((item, index) => {
        const discoverButtonUrl = discoverUrlParent && item.slug ? `${discoverUrlParent}/${item.slug}` : undefined;
        const hasLimitedOfferBadge = isLimitedOfferEnabled(item.time_to, limitedOfferLabels);
        const prizeColor = getPrizeColor(color, item.color);
        const isTierRestricted = validateTierRestriction(tier, item.levels);
        const { isNewBadgeVisible, isMoreThanOneDay, limitedTimeOfferLabel } = validateLimitedTimeBadgesData({
          __,
          newOfferDays: newBadgeData.newOfferDays,
          timeFrom: item.time_from,
          timeTo: item.time_to,
          daysLabel: limitedOfferLabels.daysLabel,
          oneDayLabel: limitedOfferLabels.oneDayLabel,
        });
        const lowestEligibleTier = item.levels?.[0];

        const isOutOfStock = item.has_coupons === false;
        const isLotteryExpired = item.time_to ? normalizeDate(item.time_to).getTime() <= new Date().getTime() : false;
        const cta = getPrizeCta({
          prizePoints: item.points,
          balance: points,
          earnPointsCtaText,
          earnPointsCtaUrl,
          ctaLabel,
          onClick: () => handleOnClick(item),
          type: item.prize_type ?? "simplified",
          redeemedTimes: item.redeemedTimes ?? 0,
          redeemLimit: item.usageLimit ?? Infinity,
          isLotteryExpired,
          intl,
        });

        const shouldShowVirtualBadges =
          !isLotteryExpired ||
          (item.prize_type !== "delayed_lottery_physical" && item.prize_type !== "delayed_lottery_simplified");

        if (isLegacy) {
          return (
            <ImageInfoBanner
              hasGradient={true}
              hideButton={shouldHideButton || isOutOfStock}
              disableButton={cta.disabled || isTierRestricted}
              title={__(item.name)}
              subtitle={__(item.subtitle)}
              key={`${item.name}_${index}`}
              text={intl.format(item.points) + " " + pointsLabel}
              bottomText={cta.label}
              backgroundImageSrc={item.images[0]}
              color={prizeColor}
              onClick={cta.onClick}
              url={cta.href}
              isPrizes={true}
              pointsProgressBar={
                <PointsProgressBar hidden={!isLogged} requiredPoints={item.points} currentPoints={points} />
              }
              discoverButtonUrl={discoverButtonUrl}
              discoverButtonText={discoverButtonText}
              size={prizeCardSizeVariant}
              hasSmallSizedButton
            >
              {shouldShowVirtualBadges && (
                <VirtualPrizeBadges
                  showNewBadge={isNewBadgeVisible}
                  isTierRestricted={isTierRestricted || !isLogged}
                  tier={lowestEligibleTier}
                  isOutOfStock={isOutOfStock}
                  hasLimitedOfferBadge={hasLimitedOfferBadge}
                  newBadgeLabel={newBadgeData.label}
                  timeLimitedBadgeLabel={limitedTimeOfferLabel}
                  isMoreThanOneDay={isMoreThanOneDay}
                  color={color}
                  outOfStockLabel={outOfStockLabel}
                />
              )}
            </ImageInfoBanner>
          );
        }
        return (
          <RewardCard
            key={`${item.name}_${index}`}
            title={__(item.name)}
            description={__(item.subtitle)}
            backgroundImageUrl={item.images[0]}
            primaryCtaLabel={cta.label}
            primaryCtaUrl={cta.href}
            primaryCtaOnClick={cta.onClick}
            isPrimaryButtonDisabled={cta.disabled || isTierRestricted}
            isPrimaryButtonHidden={shouldHideButton || isOutOfStock}
            color={prizeColor}
            size={prizeCardSizeVariant}
            variant={prizeCardVariant}
            pointsLabel={intl.format(item.points) + " " + pointsLabel}
            currentPoints={points}
            requiredPoints={item.points}
            isProgressBarHidden={!isLogged}
            discoverButtonUrl={discoverButtonUrl}
            discoverButtonLabel={discoverButtonText}
            discoverButtonOnClick={() => handleDiscoverMoreClick(item)}
            showNewBadge={isNewBadgeVisible}
            isTierRestricted={isTierRestricted || !isLogged}
            tier={lowestEligibleTier}
            isOutOfStock={isOutOfStock}
            hasLimitedOfferBadge={hasLimitedOfferBadge}
            newBadgeLabel={newBadgeData.label}
            timeLimitedBadgeLabel={limitedTimeOfferLabel}
            isMoreThanOneDay={isMoreThanOneDay}
            outOfStockLabel={outOfStockLabel}
            shouldShowVirtualBadges={shouldShowVirtualBadges}
          />
        );
      })}
    </div>
  ) : (
    <div className={styles.noItemsContainer} data-testid="simplifiedPrizes-noItems">
      <EmojiFaceSadIcon className={styles.noItemsIcon} />
      <p className={styles.noItemsHeader}>{noOffersText}</p>
      {noOffersSubtext && <p className={styles.noItemsSubheader}>{noOffersSubtext}</p>}
    </div>
  );
};

export default ItemsList;
